import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Globe, Upload, Map, Search, Edit3, Trash2,
  X, Save, RotateCcw, CheckCircle, XCircle, AlertCircle,
  ChevronDown, RefreshCw, List,
} from 'lucide-react';
import { sigApi } from '../../api/client';
import ShpImporter from '../../components/ShpImporter/ShpImporter';

/* ── Palette ──────────────────────────────────────────────── */
const C = {
  bg:       'var(--c-bg)',
  surface:  'var(--c-surface)',
  surface2: 'var(--c-surface2)',
  border:   'var(--c-border)',
  borderDk: 'var(--c-border-dk)',
  green:    'var(--c-accent)',
  amber:    'var(--c-amber)',
  red:      'var(--c-red)',
  text:     'var(--c-text)',
  muted:    'var(--c-muted)',
  dim:      'var(--c-dim)',
  a06:      'var(--c-a06)',
  a10:      'var(--c-a10)',
  a30:      'var(--c-a30)',
};

const LAYERS = [
  { id: 'region',    label: 'Régions',    color: '#3b82f6', level: 'Niveau 1' },
  { id: 'district',  label: 'Districts',  color: '#10b981', level: 'Niveau 2' },
  { id: 'commune',   label: 'Communes',   color: '#f59e0b', level: 'Niveau 3' },
  { id: 'fokontany', label: 'Fokontany',  color: '#ef4444', level: 'Niveau 4' },
];

const fmtN = n => (n == null ? '—' : Number(n).toLocaleString('fr-FR'));

/* ── Alert ────────────────────────────────────────────────── */
function Alert({ type, children }) {
  const cfg = {
    success: { bg: 'rgba(16,185,129,.1)',  border: 'rgba(16,185,129,.3)',  icon: CheckCircle, color: 'var(--c-accent)' },
    error:   { bg: 'rgba(239,68,68,.1)',   border: 'rgba(239,68,68,.3)',   icon: XCircle,     color: 'var(--c-red)'    },
    warning: { bg: 'rgba(245,158,11,.1)',  border: 'rgba(245,158,11,.3)',  icon: AlertCircle, color: 'var(--c-amber)'  },
  }[type] || {};
  const Icon = cfg.icon;
  return (
    <div style={{
      display: 'flex', gap: 10, alignItems: 'flex-start',
      background: cfg.bg, border: `1px solid ${cfg.border}`,
      borderRadius: 8, padding: '10px 14px', fontSize: 13, color: C.text,
    }}>
      {Icon && <Icon size={15} color={cfg.color} style={{ flexShrink: 0, marginTop: 1 }} />}
      <div>{children}</div>
    </div>
  );
}

/* ── Edit Modal ────────────────────────────────────────────── */
function EditModal({ item, modelType, onSave, onClose }) {
  const isRoutier = modelType === 'routier';
  const [nom,      setNom]      = useState(item.nom      || '');
  const [code,     setCode]     = useState(item.code     || '');
  const [ref,      setRef]      = useState(item.ref      || '');
  const [fclass,   setFclass]   = useState(item.fclass   || '');
  const [maxspeed, setMaxspeed] = useState(item.maxspeed != null ? String(item.maxspeed) : '');
  const [surface,  setSurface]  = useState(item.surface  || '');
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');

  const save = async () => {
    if (!isRoutier && !nom.trim()) { setError('Le nom est requis.'); return; }
    setSaving(true); setError('');
    try {
      const payload = isRoutier
        ? { nom, ref, fclass, surface, maxspeed: maxspeed !== '' ? Number(maxspeed) : null }
        : { nom, code };
      const { data } = await sigApi.update(modelType, item.id, payload);
      onSave(data);
    } catch (e) {
      setError(e.response?.data?.error || 'Erreur lors de la sauvegarde.');
    } finally {
      setSaving(false);
    }
  };

  const inp = { width: '100%', background: C.bg, border: `1px solid ${C.borderDk}`, color: C.text, borderRadius: 6, padding: '7px 10px', fontSize: 13, boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' };
  const lbl = { fontSize: 11, color: C.muted, fontWeight: 600, display: 'block', marginBottom: 4 };

  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box">
        <div className="modal-header">
          <Edit3 size={15} color="var(--ag-green)" />
          <span>Modifier</span>
          <button onClick={onClose} className="modal-close"><X size={16} /></button>
        </div>

        {isRoutier ? (
          <>
            <div style={{ marginBottom: 14 }}>
              <label style={lbl}>Nom</label>
              <input style={inp} value={nom} onChange={e => setNom(e.target.value)} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div>
                <label style={lbl}>Référence (RN, RP…)</label>
                <input style={inp} value={ref} onChange={e => setRef(e.target.value)} />
              </div>
              <div>
                <label style={lbl}>Classe (fclass)</label>
                <input style={inp} value={fclass} onChange={e => setFclass(e.target.value)} />
              </div>
              <div>
                <label style={lbl}>Vitesse max (km/h)</label>
                <input style={inp} type="number" value={maxspeed} onChange={e => setMaxspeed(e.target.value)} />
              </div>
              <div>
                <label style={lbl}>Surface</label>
                <input style={inp} value={surface} onChange={e => setSurface(e.target.value)} />
              </div>
            </div>
          </>
        ) : (
          <>
            <div style={{ marginBottom: 14 }}>
              <label style={lbl}>Nom *</label>
              <input style={inp} value={nom} onChange={e => setNom(e.target.value)} />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={lbl}>Code</label>
              <input style={inp} value={code} onChange={e => setCode(e.target.value)} />
            </div>
          </>
        )}

        {error && <div style={{ color: C.red, fontSize: 12, marginBottom: 12 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} className="modal-btn-ghost">Annuler</button>
          <button onClick={save} disabled={saving} className="modal-btn-primary">
            <Save size={13} /> {saving ? 'Sauvegarde…' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Delete Modal ────────────────────────────────────────── */
function DeleteModal({ item, modelType, onConfirm, onClose }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await onConfirm(item.id); }
    finally { setBusy(false); }
  };
  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ borderColor: 'rgba(239,68,68,0.3)', maxWidth: 380 }}>
        <div className="modal-header">
          <Trash2 size={15} color={C.red} />
          <span>Supprimer</span>
          <button onClick={onClose} className="modal-close"><X size={16} /></button>
        </div>
        <p style={{ fontSize: 13, color: C.muted, margin: '0 0 20px' }}>
          Supprimer définitivement <strong style={{ color: C.text }}>{item.nom}</strong>
          {item.code ? ` (${item.code})` : ''} ? Cette action est irréversible.
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} className="modal-btn-ghost">Annuler</button>
          <button onClick={go} disabled={busy} className="modal-btn-danger">
            <Trash2 size={13} /> {busy ? 'Suppression…' : 'Supprimer'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Annuaire (liste + recherche + edit + delete) ────────── */
function Annuaire({ modelType, color, onCountChange }) {
  const [items,    setItems]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');
  const [editItem, setEditItem] = useState(null);
  const [delItem,  setDelItem]  = useState(null);
  const [page,     setPage]     = useState(1);
  const PAGE_SIZE = 50;

  // Ref stable pour éviter que onCountChange (inline dans le parent)
  // ne déclenche une boucle infinie via load → useEffect → load…
  const onCountChangeRef = useRef(onCountChange);
  useEffect(() => { onCountChangeRef.current = onCountChange; });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await sigApi.list(modelType);
      const results = data.results || [];
      setItems(results);
      onCountChangeRef.current?.(data.count ?? results.length);
    } catch (e) {
      console.error('load limites :', e);
    } finally {
      setLoading(false);
    }
  }, [modelType]);  // onCountChange retiré des dépendances

  useEffect(() => { load(); setPage(1); }, [load]);

  const isRoutier = modelType === 'routier';

  const filtered = search.trim()
    ? items.filter(it => {
        const q = search.toLowerCase();
        if (isRoutier) {
          return it.nom?.toLowerCase().includes(q) ||
                 it.ref?.toLowerCase().includes(q) ||
                 it.fclass?.toLowerCase().includes(q);
        }
        return it.nom?.toLowerCase().includes(q) ||
               it.code?.toLowerCase().includes(q);
      })
    : items;

  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);

  const handleSave = updated => {
    setItems(prev => prev.map(it => it.id === updated.id ? { ...it, ...updated } : it));
    setEditItem(null);
  };

  const handleDelete = async id => {
    await sigApi.remove(modelType, id);
    setItems(prev => prev.filter(it => it.id !== id));
    onCountChangeRef.current?.(c => Math.max(0, c - 1));
    setDelItem(null);
  };

  return (
    <>
      {editItem && (
        <EditModal item={editItem} modelType={modelType} onSave={handleSave} onClose={() => setEditItem(null)} />
      )}
      {delItem && (
        <DeleteModal item={delItem} modelType={modelType} onConfirm={handleDelete} onClose={() => setDelItem(null)} />
      )}

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14 }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <Search size={13} color={C.muted} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
          <input
            type="text"
            placeholder="Rechercher nom, code…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            style={{
              width: '100%', background: C.surface2, border: `1px solid ${C.border}`,
              color: C.text, borderRadius: 8, padding: '7px 10px 7px 30px',
              fontSize: 12, boxSizing: 'border-box', outline: 'none',
            }}
          />
          {search && (
            <button onClick={() => setSearch('')} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: C.muted, display: 'flex' }}>
              <X size={12} />
            </button>
          )}
        </div>
        <button onClick={load} style={{ background: 'none', border: `1px solid ${C.border}`, color: C.muted, borderRadius: 8, padding: '7px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontSize: 12 }}>
          <RefreshCw size={12} /> Actualiser
        </button>
      </div>

      <div style={{ fontSize: 11, color: C.muted, marginBottom: 8 }}>
        {loading ? 'Chargement…' : `${filtered.length} / ${items.length} entrée(s)`}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: C.muted, fontSize: 13 }}>Chargement…</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: C.dim, fontSize: 13 }}>
          {items.length === 0 ? 'Aucune donnée importée.' : 'Aucun résultat.'}
        </div>
      ) : (
        <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: C.surface2 }}>
                <th style={{ padding: '8px 12px', textAlign: 'left', color: C.muted, fontWeight: 600, borderBottom: `1px solid ${C.border}` }}>Nom</th>
                {isRoutier ? (
                  <>
                    <th style={{ padding: '8px 12px', textAlign: 'left', color: C.muted, fontWeight: 600, borderBottom: `1px solid ${C.border}`, width: 70 }}>Réf.</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left', color: C.muted, fontWeight: 600, borderBottom: `1px solid ${C.border}`, width: 90 }}>Classe</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left', color: C.muted, fontWeight: 600, borderBottom: `1px solid ${C.border}`, width: 70 }}>Vit. max</th>
                  </>
                ) : (
                  <th style={{ padding: '8px 12px', textAlign: 'left', color: C.muted, fontWeight: 600, borderBottom: `1px solid ${C.border}`, width: 80 }}>Code</th>
                )}
                <th style={{ padding: '8px 12px', textAlign: 'right', color: C.muted, fontWeight: 600, borderBottom: `1px solid ${C.border}`, width: 80 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map((it, i) => (
                <tr key={it.id} style={{ borderBottom: i < pageItems.length - 1 ? `1px solid ${C.a06}` : 'none' }}>
                  <td style={{ padding: '8px 12px', color: C.text }}>
                    <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: color, marginRight: 8, verticalAlign: 'middle' }} />
                    {it.nom || <span style={{ color: C.dim, fontStyle: 'italic' }}>—</span>}
                  </td>
                  {isRoutier ? (
                    <>
                      <td style={{ padding: '8px 12px', color: C.muted, fontFamily: 'monospace', fontSize: 11 }}>{it.ref || '—'}</td>
                      <td style={{ padding: '8px 12px', color: C.muted, fontSize: 11 }}>{it.fclass || '—'}</td>
                      <td style={{ padding: '8px 12px', color: C.muted, fontSize: 11 }}>{it.maxspeed != null ? `${it.maxspeed} km/h` : '—'}</td>
                    </>
                  ) : (
                    <td style={{ padding: '8px 12px', color: C.muted, fontFamily: 'monospace', fontSize: 11 }}>{it.code || '—'}</td>
                  )}
                  <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                    <div style={{ display: 'inline-flex', gap: 6 }}>
                      <button
                        onClick={() => setEditItem(it)}
                        title="Modifier"
                        style={{ background: 'rgba(99,102,241,0.12)', border: 'none', color: '#818cf8', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                      ><Edit3 size={11} /></button>
                      <button
                        onClick={() => setDelItem(it)}
                        title="Supprimer"
                        style={{ background: 'rgba(239,68,68,0.1)', border: 'none', color: '#f87171', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                      ><Trash2 size={11} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 14 }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ background: C.surface2, border: `1px solid ${C.border}`, color: C.muted, borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontSize: 12 }}>‹</button>
          <span style={{ fontSize: 12, color: C.muted, padding: '4px 8px' }}>{page} / {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={{ background: C.surface2, border: `1px solid ${C.border}`, color: C.muted, borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontSize: 12 }}>›</button>
        </div>
      )}
    </>
  );
}

/* ── Page principale ──────────────────────────────────────── */
export default function LimitesAdmin({ initialLayer = 'region' }) {
  const validInit = LAYERS.some(l => l.id === initialLayer) ? initialLayer : 'region';
  const [activeLayer,    setActiveLayer]    = useState(validInit);
  const [tab,            setTab]            = useState('import');   // 'import' | 'annuaire'
  const [stats,          setStats]          = useState({});
  const [showImporter,   setShowImporter]   = useState(false);

  const handleCountChange = useCallback((cnt) => {
    setStats(s => ({
      ...s,
      [activeLayer]: typeof cnt === 'function' ? cnt(s[activeLayer]) : cnt,
    }));
  }, [activeLayer]);

  const layer = LAYERS.find(l => l.id === activeLayer);

  // Chargement des stats réelles
  useEffect(() => {
    sigApi.stats()
      .then(({ data }) => setStats(data))
      .catch(() => {});
  }, []);

  const refreshStats = () => {
    sigApi.stats().then(({ data }) => setStats(data)).catch(() => {});
  };

  return (
    <div style={{ height: '100%', overflow: 'auto', padding: 28, background: C.bg, color: C.text, fontFamily: 'Inter, system-ui, sans-serif' }}>

      {/* Modal ShpImporter */}
      {showImporter && (
        <ShpImporter
          onClose={() => { setShowImporter(false); refreshStats(); }}
        />
      )}

      {/* En-tête */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 10 }}>
            <Globe size={20} color={C.green} /> Limites administratives
          </div>
          <div style={{ fontSize: 13, color: C.muted }}>
            Importer, consulter et gérer les découpages administratifs de Madagascar
          </div>
        </div>
        <button
          onClick={() => setShowImporter(true)}
          style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '8px 16px', background: 'rgba(129,140,248,0.12)', border: '1px solid #6366f1', color: '#818cf8', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
        >
          <Upload size={14} /> Importer shapefile
        </button>
      </div>

      {/* Sélecteur de couche avec vrais compteurs */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 24, flexWrap: 'wrap' }}>
        {LAYERS.map(l => (
          <button
            key={l.id}
            onClick={() => { setActiveLayer(l.id); setTab('import'); }}
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
              padding: '12px 18px', borderRadius: 10, cursor: 'pointer', minWidth: 140,
              border: `1px solid ${activeLayer === l.id ? l.color : C.border}`,
              background: activeLayer === l.id ? `${l.color}15` : C.surface2,
              color: activeLayer === l.id ? l.color : C.muted,
              transition: 'all .15s',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <Map size={13} />
              <span style={{ fontWeight: 700, fontSize: 13 }}>{l.label}</span>
            </div>
            <span style={{ fontSize: 11, color: activeLayer === l.id ? `${l.color}cc` : C.dim }}>{l.level}</span>
            <span style={{
              marginTop: 8, fontSize: 18, fontWeight: 800,
              color: stats[l.id] > 0 ? l.color : C.dim,
            }}>
              {stats[l.id] != null ? fmtN(stats[l.id]) : '—'}
            </span>
            <span style={{ fontSize: 10, color: C.dim }}>entrée(s) en BD</span>
          </button>
        ))}
      </div>

      {/* Panel principal */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>

        {/* Header du panel */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 20px', borderBottom: `1px solid ${C.border}`, background: C.surface2 }}>
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: layer?.color }} />
          <span style={{ fontWeight: 700, fontSize: 14, color: C.text }}>{layer?.label}</span>
          <span style={{ fontSize: 12, color: C.muted }}>— {layer?.level}</span>
          {stats[activeLayer] > 0 && (
            <span style={{ marginLeft: 'auto', fontSize: 12, color: layer?.color, fontWeight: 700, background: `${layer?.color}15`, padding: '2px 10px', borderRadius: 20 }}>
              {fmtN(stats[activeLayer])} entrée(s)
            </span>
          )}
        </div>

        {/* Onglets Import / Annuaire */}
        <div style={{ display: 'flex', borderBottom: `1px solid ${C.border}` }}>
          {[
            { id: 'import',    icon: Upload, label: 'Importer'  },
            { id: 'annuaire',  icon: List,   label: 'Annuaire'  },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '10px 18px', border: 'none', cursor: 'pointer', fontSize: 13,
                background: tab === t.id ? C.surface : 'transparent',
                color: tab === t.id ? C.text : C.muted,
                borderBottom: tab === t.id ? `2px solid ${layer?.color}` : '2px solid transparent',
                fontWeight: tab === t.id ? 600 : 400,
                transition: 'all .15s',
              }}
            >
              <t.icon size={13} /> {t.label}
            </button>
          ))}
        </div>

        <div style={{ padding: 24 }}>
          {tab === 'import' && (
            <div>
              <div style={{ marginBottom: 16, fontSize: 13, color: C.muted }}>
                Importez un fichier Shapefile (.zip contenant .shp + .dbf + .shx) pour les <strong style={{ color: C.text }}>{layer?.label}</strong>. L'outil lit automatiquement les colonnes et vous permet d'assigner les correspondances.
              </div>
              <button
                onClick={() => setShowImporter(true)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '40px 24px', width: '100%', justifyContent: 'center',
                  background: 'transparent', border: `2px dashed ${C.border}`,
                  borderRadius: 12, cursor: 'pointer', color: C.muted,
                  fontSize: 14, transition: 'all .2s', flexDirection: 'column',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = layer?.color; e.currentTarget.style.color = layer?.color; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.muted; }}
              >
                <Upload size={28} style={{ marginBottom: 8 }} />
                Déposer ou sélectionner un fichier Shapefile
                <span style={{ fontSize: 12, marginTop: 4 }}>ZIP contenant .shp, .dbf, .shx</span>
              </button>
            </div>
          )}

          {tab === 'annuaire' && (
            <Annuaire
              key={activeLayer}
              modelType={activeLayer}
              color={layer?.color}
              onCountChange={handleCountChange}
            />
          )}
        </div>
      </div>

      {/* Info hiérarchie */}
      <div style={{ marginTop: 20, background: C.surface2, borderRadius: 10, border: `1px solid ${C.border}`, padding: '14px 18px', fontSize: 12, color: C.muted }}>
        <strong style={{ color: C.text }}>Hiérarchie administrative Madagascar</strong>
        <div style={{ marginTop: 6, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {LAYERS.map((l, i, arr) => (
            <React.Fragment key={l.id}>
              <span style={{ color: l.color, fontWeight: 600 }}>{l.label}</span>
              {i < arr.length - 1 && <span style={{ color: C.dim }}>→</span>}
            </React.Fragment>
          ))}
        </div>
        <div style={{ marginTop: 6 }}>
          Importez dans l'ordre hiérarchique pour associer correctement les liaisons parent/enfant (District → Région, Commune → District, etc.).
        </div>
      </div>
    </div>
  );
}
